import React from 'react';
import { Section } from 'shared/ui/Section';
import { Field } from 'shared/ui/Field';
import { Btn } from 'shared/ui/Btn';
import { Toggle2 } from 'shared/ui/Toggle2';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'shared/ui/Select';
import { useQuoteDetailsSection, type QuoteFieldName } from './useQuoteDetailsSection';

const INPUT = 'h-9 w-full rounded-[var(--radius-2)] border border-hairline/30 bg-surface-overlay px-3 font-body text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand/60 disabled:opacity-50';

export function QuoteDetailsSection({ id }: { id: string }) {
  const s = useQuoteDetailsSection();

  /** Field ids are `field-<name>` so Phase 7's readiness links can focus them. */
  const text = (name: QuoteFieldName, label: string, opts: { required?: boolean; type?: string; maxLength?: number; placeholder?: string; hint?: string; disabled?: boolean } = {}) => {
    const stored = (s.metadata as unknown as Record<string, unknown>)[name];
    // `||`, not `??`, for the expiration fallback — classic is
    // `metadata.quoteExpirationDate || calculateDefaultExpirationDate()`
    // (App.js:1565), so an EMPTY STRING (a user who cleared the field, or a
    // loaded quote that stored one) falls back to the calculated default
    // rather than rendering blank.
    const value = name === 'quoteExpirationDate' ? String(stored || s.defaultExpiration) : String(stored ?? '');
    return (
      <Field label={label} htmlFor={`field-${name}`} required={opts.required} error={s.quoteErrors[name]} hint={opts.hint}>
        <input
          id={`field-${name}`}
          type={opts.type ?? 'text'}
          maxLength={opts.maxLength}
          placeholder={opts.placeholder}
          disabled={opts.disabled}
          value={value}
          onChange={(e) => s.set(name, e.target.value)}
          onBlur={(e) => s.setFieldError(name, e.target.value)}
          className={INPUT}
        />
      </Field>
    );
  };

  return (
    <Section.Root id={id} defaultOpen>
      <Section.Bar
        title="Quote Details"
        role="Customer, opportunity, dates"
        summary={s.summary}
        trailing={s.showCci
          ? <Toggle2 leftLabel="Standard" rightLabel="CCI" ariaLabel="Quote path" isRightActive={s.cciEnabled} onToggle={s.onCciToggle}
              disabled={Boolean(s.cciDisabledReason)} title={s.cciDisabledReason ?? undefined} />
          : undefined}
      />
      <Section.Body>
        <div className="grid gap-4 md:grid-cols-3">
          <Field label="Account Executive" htmlFor="field-accountExecutive" required error={s.quoteErrors.accountExecutive} hint={s.accountExecutiveEmail as string}>
            <Select value={String(s.metadata.accountExecutive ?? '')} onValueChange={s.onAccountExecutiveChange}>
              <SelectTrigger id="field-accountExecutive" aria-label="Account Executive"><SelectValue placeholder="Select Account Executive" /></SelectTrigger>
              <SelectContent>
                {s.accountExecutives.map((e) => <SelectItem key={e.email} value={e.name}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          {text('opportunityID', 'Opportunity ID', { required: true, maxLength: 18, placeholder: '18 alphanumeric characters' })}
          {text('quoteDescription', 'Quote Description', { hint: 'Shown in the Save/Load list' })}
          {text('preparedFor', 'Customer Name', { required: true })}
          {text('salesforceQuoteNumber', 'Salesforce Quote #', { required: true, placeholder: '5 digits' })}
          {text('notes', 'Notes', { hint: 'Printed in the quote Notes section' })}
          {text('partnerName', 'Partner Name', { hint: 'Optional' })}
          {text('warrantyStartDate', 'Warranty Start Date', { type: 'date', hint: 'Optional' })}
          {text('quoteExpirationDate', 'Quote Expires', { type: 'date' })}
        </div>

        <div className="mt-6 flex items-center gap-3">
          <Btn variant="contained" onClick={s.onGenerate} disabled={Boolean(s.busy)}>Generate Quote PDF</Btn>
          <span className="font-body text-xs text-ink-muted">
            {s.busy ?? 'Saves the quote, renders the PDF and opens the CSV export.'}
          </span>
        </div>
      </Section.Body>
    </Section.Root>
  );
}
